import { Gem, HeartHandshake, Loader2 } from 'lucide-react';
import type { NavigatorCategory } from './navigator-results.types';

type NavigatorCategoryTabsProps = {
  categories: NavigatorCategory[];
  activeIndex: number;
  onChange: (index: number) => void;
  /** Matchmaking still running — an empty category is pending, not a dead end. */
  isMatching?: boolean;
  /** Best Match override for the ticket-10 prototype's mock scenarios. */
  bestMatchProviderIds?: ReadonlySet<string>;
};

export const NavigatorCategoryTabs = ({
  categories,
  activeIndex,
  onChange,
  isMatching = false,
  bestMatchProviderIds,
}: NavigatorCategoryTabsProps) => {
  if (categories.length === 0) return null;

  return (
    <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" role="tablist">
      {categories.map((category, index) => {
        const isActive = index === activeIndex;
        const total = category.providerCount ?? category.providers.length;
        // Featured = the selection (selected-first ordering). Label it
        // "Selected" once the user swapped away from the AI top match.
        const featured =
          category.providers.find((provider) => provider.isSelected) ?? category.providers[0];
        const featuredName = featured?.name || null;
        const isSwappedSelection =
          Boolean(featured?.isSelected) && featured?.rankOrder != null && featured.rankOrder !== 1;
        // Best Match (feature 087, ticket 15): this bucket contains the
        // plan-wide Best Match — read from the live per-provider flag, with
        // `bestMatchProviderIds` as the ticket-10 prototype's fallback.
        const hasBestMatch = category.providers.some(
          (provider) => provider.isBestMatch ?? bestMatchProviderIds?.has(provider.id) ?? false,
        );

        return (
          <button
            key={category.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(index)}
            className={`relative flex min-w-[220px] flex-col items-start gap-1 rounded-xl border px-4 py-3 text-left transition-all duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis ${
              isActive
                ? 'border-sage bg-sage-bg shadow-[0_2px_8px_rgba(15,23,42,0.06)]'
                : 'border-border-subtle bg-card-base hover:border-sage'
            }`}
          >
            <div className="flex w-full items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 font-termina text-sm font-medium text-text-primary">
                {category.name}
                {hasBestMatch && (
                  <span
                    title="Contains a Best Match"
                    aria-label="Contains a Best Match"
                    className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border border-[hsl(38_55%_68%)]/60 bg-gradient-to-br from-[hsl(43_75%_94%)] to-[hsl(30_68%_78%)] text-[hsl(30_65%_26%)]"
                  >
                    <Gem className="h-2.5 w-2.5" aria-hidden />
                  </span>
                )}
              </span>
              {category.isPrimary ? (
                <span className="rounded-full bg-sage px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  Primary
                </span>
              ) : (
                <span className="rounded-full border border-border-subtle px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-text-tertiary">
                  Secondary
                </span>
              )}
            </div>
            <span className="text-xs text-text-secondary">
              {total === 0 && isMatching ? (
                // Its search has not run yet — announcing a concierge hand-off
                // here would claim we looked and found nobody.
                <span className="inline-flex items-center gap-1 font-medium text-text-tertiary">
                  <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
                  Finding providers…
                </span>
              ) : total === 0 ? (
                // No matches → the detail panel hands off to the Care
                // Concierge; the chip mirrors that instead of a bleak "0 options".
                <span className="inline-flex items-center gap-1 font-medium text-sage">
                  <HeartHandshake className="h-3.5 w-3.5" aria-hidden />
                  Concierge assist
                </span>
              ) : (
                <>
                  {total} option{total === 1 ? '' : 's'}
                  {featuredName && (
                    <>
                      <span className="mx-1.5 text-text-tertiary">·</span>
                      {isSwappedSelection ? 'Selected: ' : 'Top match: '}
                      <span className="font-medium text-text-primary">{featuredName}</span>
                    </>
                  )}
                </>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};
